import { SelectOption } from '@chronosphereio/chrono-ui';
import { DEFAULT_ALL_VALUE } from '@chronosphereio/core';
import { useMemo } from 'react';
import { VariableOption } from '../../../core';

/**
 * Hook that contains some common logic for single/multi select inputs for template variables.
 */
export function useTemplateVariableOptions(allowAllValue: boolean, options?: VariableOption[]) {
  // Make it easier to find selected options by using a Map to index them by key
  const optionsByKey = useMemo(() => {
    const map = new Map<string, SelectOption>();

    if (options === undefined) {
      return map;
    }

    // Map remembers insertion order so add All value first if enabled
    if (allowAllValue) {
      map.set(DEFAULT_ALL_VALUE, { key: DEFAULT_ALL_VALUE, label: 'All' });
    }

    for (const option of options) {
      map.set(option.value, { key: option.value, label: option.label });
    }

    return map;
  }, [options, allowAllValue]);

  // Control expects an array of SelectOptions, so convert the Map
  const selectOptions = useMemo(() => Array.from(optionsByKey.values()), [optionsByKey]);

  // Boolean to indicate if options have been loaded.
  // We do not count "All" as a loaded option, so if there is only one option (All) then we have not loaded options.
  const hasLoadedOptions = useMemo(() => {
    if (!allowAllValue) {
      return selectOptions.length > 0;
    } else {
      return selectOptions.length > 1;
    }
  }, [selectOptions, allowAllValue]);

  return { optionsByKey, selectOptions, hasLoadedOptions };
}
